import { forwardRef, useImperativeHandle, useRef } from 'react';
import { SectionLabel } from '@/components/ui/section-label';
import { Card } from '@/components/ui/card/Card';

import { DropDownList } from '@/components/form/inputs/DropDownList';
import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import { useTranslation } from '@/hooks/useTranslation';
import { Droplets } from 'lucide-react';

import { lubricationTypes } from '../../data/lubricationOptions';

type LubTabProps = {
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
};

export const LubTab = forwardRef(function LubTab({ values, onChange }: LubTabProps, ref) {
  const ddInput1Ref = useRef<{ validate: () => boolean }>(null);
  const input1Ref = useRef<{ validate: () => boolean }>(null);
  const { t } = useTranslation();

  useImperativeHandle(ref, () => ({
    validate: () => {
      const v1 = ddInput1Ref.current?.validate() ?? true;
      const v2 = input1Ref.current?.validate() ?? true;
      return v1 && v2;
    },
  }));

  return (
    <div className="grid grid-cols-1 gap-5">
      <Card padding="sm">
        <div className="flex flex-col gap-2">
          <div className="space-y-1">
            <SectionLabel icon={Droplets}>{t('traceability.lubricationTab.title')}</SectionLabel>
            <p className="text-sm text-muted-foreground">
              {t('traceability.lubricationTab.description')}
            </p>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <DropDownList
              ref={ddInput1Ref}
              name="lubType"
              label={t('traceability.lubricationTab.typeLabel')}
              value={(values['lubType'] as string) ?? ''}
              onChange={(val) => onChange('lubType', val)}
              options={lubricationTypes.map(({ value, labelKey }) => ({
                value,
                label: t(labelKey),
              }))}
              required
              side="bottom"
            />
            <SimpleInput
              ref={input1Ref}
              name="lubBrand"
              label={t('traceability.lubricationTab.brandLabel')}
              value={(values['lubBrand'] as string) ?? ''}
              onChange={(val) => onChange('lubBrand', val)}
              placeholder={t('traceability.lubricationTab.brandPlaceholder')}
            />
          </div>
        </div>
      </Card>
    </div>
  );
});
